/**
 * Document query key factory and list params.
 */
import type { ListReusableTemplatesParams } from '@/services/documents/reusableTemplateService';

import type { DocumentsContentView, FilterGroup, SidebarSelection } from './documentPageTypes';

export interface DocumentsListScopeParams {
  sidebarSelection: SidebarSelection;
  activeTab: string;
  currentView: DocumentsContentView;
  filterGroups: FilterGroup[];
  pageSize: number;
  /** SE3: when false, Overview omits invoice/proposal type fetches (Member/Collaborator). */
  includeFinancialDocumentTypes: boolean;
  /** When true, skip the documents list query (e.g. Documents → Settings view). */
  skipListFetch?: boolean;
}

export interface DocumentsListParams extends DocumentsListScopeParams {
  page: number;
}

export const documentsQueryKeys = {
  all: ['documents'] as const,
  lists: () => [...documentsQueryKeys.all, 'list'] as const,
  listScope: (scope: DocumentsListScopeParams) =>
    [...documentsQueryKeys.lists(), 'scope', JSON.stringify(scope)] as const,
  list: (params: DocumentsListParams) =>
    [...documentsQueryKeys.lists(), JSON.stringify(params)] as const,
  details: () => [...documentsQueryKeys.all, 'detail'] as const,
  detail: (id: string) => [...documentsQueryKeys.details(), id] as const,
  activityFileName: (id: string) =>
    [...documentsQueryKeys.details(), id, 'activity-file-name'] as const,
  htmlContent: (id: string) => [...documentsQueryKeys.details(), id, 'html-content'] as const,
  htmlPageContent: (id: string, pageId: string) =>
    [...documentsQueryKeys.details(), id, 'html-content', pageId] as const,
  shares: (id: string) => [...documentsQueryKeys.details(), id, 'shares'] as const,
  shareMergeTagWarnings: (
    documentId: string,
    contactIdsKey: string,
    htmlFingerprint: string,
    emailFingerprint: string,
    projectId: string
  ) =>
    [
      ...documentsQueryKeys.details(),
      documentId,
      'share-merge-tag-warnings',
      contactIdsKey,
      htmlFingerprint,
      emailFingerprint,
      projectId,
    ] as const,
  shareJobs: () => [...documentsQueryKeys.all, 'share-job'] as const,
  shareJob: (documentId: string, jobId: string) =>
    [...documentsQueryKeys.shareJobs(), documentId, jobId] as const,
  shareJobDisabled: (disabledScopeId: string) =>
    [...documentsQueryKeys.shareJobs(), 'disabled', disabledScopeId] as const,
  folders: (scope: 'documents' | 'emails' = 'documents') =>
    [...documentsQueryKeys.all, 'folders', scope] as const,
};

export const documentSigningStatusQueryKeys = {
  all: ['document-signing-status'] as const,
  detail: (documentId: string) => [...documentSigningStatusQueryKeys.all, documentId] as const,
};

export const countersignerPreviewQueryKeys = {
  all: ['countersigner-preview'] as const,
  detail: (documentId: string) => [...countersignerPreviewQueryKeys.all, documentId] as const,
};

export const reusableTemplateKeys = {
  all: ['reusable-templates'] as const,
  list: (params: ListReusableTemplatesParams) =>
    [...reusableTemplateKeys.all, 'list', params] as const,
};

export const formResponseQueryKeys = {
  all: ['form-responses'] as const,
  details: () => [...formResponseQueryKeys.all, 'detail'] as const,
  detail: (submissionId: string) => [...formResponseQueryKeys.details(), submissionId] as const,
};
